import { escapeHtml as esc } from './escapeHtml.js';
import { mlvApplyZ } from './mlvZLayers.js';
import { mlvSaveFocus } from './mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../navigation/mlvModalHistoryBridge.js';

/**
 * Calificación 1–5 estrellas (ventas o tarjeta de servicios si `submitEstrellas` aporta el envío).
 */
export function openCalificarVendedorModal({
  vendedorUid,
  vendedorNombre = 'tu vendedor',
  productoId = '',
  calificacionesRepository,
  /** @type {undefined | ((estrellas: number) => Promise<void>)} */
  submitEstrellas,
  titulo,
  /** HTML ya escapado (p. ej. con <strong>); si falta se usa copy de compra marketplace. */
  cuerpoHtml,
  textoGraciasDetalle,
  invalidateCatalogoCache,
  onDespuesDeEnviar,
} = {}) {
  return new Promise((resolve) => {
    const restoreFocus = mlvSaveFocus();
    const prev = document.getElementById('mlv-calif-vend-modal');
    if (prev) prev.remove();
    const wrap = document.createElement('div');
    wrap.id = 'mlv-calif-vend-modal';
    wrap.className =
      'fixed inset-0 flex items-end justify-center bg-black/55 p-4 sm:items-center';
    mlvApplyZ(wrap, 'calificarVendedor');
    const nombre = esc(String(vendedorNombre || 'tu vendedor').trim() || 'tu vendedor');
    const tit = esc(
      String(
        titulo != null ? titulo : '¿Cómo te fue con esta compra?'
      ).trim() || '¿Cómo te fue con esta compra?'
    );
    const body =
      cuerpoHtml != null && String(cuerpoHtml).trim()
        ? String(cuerpoHtml)
        : `<p class="mt-2 text-sm text-slate-600">Tu opinión sobre <strong class="text-slate-800">${nombre}</strong> ayuda a otros vecinos a elegir con confianza. <strong class="text-slate-800">Una calificación por compra.</strong></p>`;
    wrap.innerHTML = `
      <div class="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-200/80 sm:rounded-3xl">
        <h3 class="font-heading text-lg font-bold text-slate-900">${tit}</h3>
        ${body.startsWith('<') ? body : `<p class="mt-2 text-sm text-slate-600">${body}</p>`}
        <div class="mt-4 flex flex-wrap justify-center gap-2" role="group" aria-label="Estrellas">
          ${[1, 2, 3, 4, 5]
            .map(
              (s) =>
                `<button type="button" class="mlv-calif-star touch-manipulation rounded-xl border-2 border-amber-400/90 bg-white px-3.5 py-2 text-sm font-extrabold text-amber-800 shadow-sm transition hover:bg-amber-100 disabled:opacity-50" data-s="${s}">${s} ★</button>`
            )
            .join('')}
        </div>
        <p id="mlv-calif-vend-msg" class="mt-2 min-h-[1rem] text-center text-xs text-red-600"></p>
        <button type="button" id="mlv-calif-vend-cancel" class="mt-4 w-full rounded-full border border-slate-300 py-2.5 text-sm font-semibold text-slate-700">Cerrar</button>
      </div>
    `;
    const hist = mlvModalHistoryAttach(() => {
      document.removeEventListener('keydown', onKey);
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      resolve();
    });
    function close() {
      hist.closeFromUi();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    function onBackdropClick(e) {
      if (e.target === wrap) close();
    }
    wrap.addEventListener('click', onBackdropClick);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
    const cancelBtn = wrap.querySelector('#mlv-calif-vend-cancel');
    cancelBtn?.addEventListener('click', close);
    requestAnimationFrame(() =>
      wrap.querySelector('.mlv-calif-star')?.focus?.({ preventScroll: true })
    );

    const graciasExtra = esc(
      String(textoGraciasDetalle || 'Con tu reseña el barrio compra más seguro.').trim()
    );

    function showGraciasPantalla() {
      wrap.removeEventListener('click', onBackdropClick);
      wrap.innerHTML = `
      <div class="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-200/80 sm:rounded-3xl">
        <h3 class="font-heading text-lg font-bold text-slate-900">Gracias por tu calificación</h3>
        <p class="mt-2 text-sm leading-relaxed text-slate-600">${graciasExtra}</p>
        <button type="button" id="mlv-calif-thanks-ok" class="mt-6 w-full rounded-full bg-brand-primary py-3.5 text-sm font-bold text-white shadow-md transition hover:brightness-95">
          Listo
        </button>
      </div>`;
      function thanksBackdrop(e) {
        if (e.target === wrap) close();
      }
      wrap.addEventListener('click', thanksBackdrop);
      wrap.querySelector('#mlv-calif-thanks-ok')?.addEventListener('click', close);
      requestAnimationFrame(() =>
        wrap.querySelector('#mlv-calif-thanks-ok')?.focus?.({ preventScroll: true })
      );
    }

    function puedeEnviar() {
      if (typeof submitEstrellas === 'function') return true;
      return Boolean(calificacionesRepository);
    }

    wrap.querySelectorAll('.mlv-calif-star').forEach((btn) => {
      btn.addEventListener(
        'click',
        async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const s = Number(btn.getAttribute('data-s'));
          const msgEl = wrap.querySelector('#mlv-calif-vend-msg');
          if (!Number.isInteger(s) || s < 1 || s > 5 || !puedeEnviar()) return;
          if (msgEl) msgEl.textContent = '';
          wrap.querySelectorAll('.mlv-calif-star').forEach((b) => {
            b.disabled = true;
          });
          if (cancelBtn) cancelBtn.disabled = true;
          try {
            if (typeof submitEstrellas === 'function') {
              await submitEstrellas(s);
            } else {
              await calificacionesRepository.enviarCalificacionVendedor({
                vendedorUid: String(vendedorUid || '').trim(),
                estrellas: s,
                productoId: String(productoId || '').trim(),
              });
            }
            try {
              invalidateCatalogoCache?.();
            } catch (_e) {
              /* ignore */
            }
            try {
              await onDespuesDeEnviar?.();
            } catch (_e) {
              /* ignore */
            }
            showGraciasPantalla();
          } catch (err) {
            console.error(err);
            const code = String(err?.code || '');
            if (code.includes('already-exists')) {
              try {
                await onDespuesDeEnviar?.();
              } catch (_e) {
                /* ignore */
              }
              showGraciasPantalla();
              return;
            }
            let t = err?.message || 'No se pudo enviar.';
            if (code.includes('deadline-exceeded')) {
              t = 'Pasaron más de 7 días desde que publicaron la tarjeta. Ya no se puede calificar.';
            } else if (code.includes('permission-denied') || code.includes('failed-precondition')) {
              t = 'No podés calificar esta tarjeta en este momento.';
            }
            if (msgEl) msgEl.textContent = t;
            wrap.querySelectorAll('.mlv-calif-star').forEach((b) => {
              b.disabled = false;
            });
            if (cancelBtn) cancelBtn.disabled = false;
          }
        },
        true
      );
    });
  });
}
